import { Calculator } from "lucide-react";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "src/components/ui/accordion";
import { Badge } from "src/components/ui/badge";
import { Card, CardContent } from "src/components/ui/card";

const ranges = [
  {
    range: "< 1.0×",
    background:
      "linear-gradient(90deg in oklch, var(--chart-bad), var(--chart-middle))",
    title: "Acquisition not repaid",
    description:
      "Gross profit hasn’t covered acquisition costs. Improve margins or lower nCAC before increasing spend.",
  },
  {
    range: "1.0 - 2.0×",
    background: "var(--chart-middle)",
    title: "Limited headroom",
    description:
      "Acquisition costs are covered, with little left for overheads and growth.",
  },
  {
    range: "2.0 - 3.0×",
    background:
      "linear-gradient(90deg in oklch, var(--chart-middle), var(--chart-good))",
    title: "Healthy acquisition",
    description:
      "More profit remains to fund growth. Watch whether efficiency holds as spend increases.",
  },
  {
    range: "> 3.0×",
    background: "var(--chart-good)",
    title: "Potential to scale",
    description:
      "Strong efficiency may support more investment. Check demand and cash capacity first.",
  },
];

export function CohortMetricGuide({ numberFormat }) {
  const one = numberFormat.currency(1, 0);
  const two = numberFormat.currency(2, 0);

  return (
    <Card className="@container/guide w-full">
      <CardContent className="space-y-6 sm:px-7">
        <div className="space-y-3">
          <h2 className="text-xl font-semibold tracking-tight sm:text-2xl">
            How to read 30-Day LTV:nCAC
          </h2>
          <p className="text-muted-foreground text-sm leading-relaxed">
            See how much gross profit a customer generates in their first 30
            days for every {one} spent acquiring them.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-3 @min-[480px]/guide:grid-cols-2 @min-[1000px]/guide:grid-cols-4">
          {ranges.map(({ range, title, description, background }) => (
            <div
              key={range}
              className="bg-foreground/[0.03] rounded-lg border border-border/60 p-5"
            >
              <Badge
                variant="outline"
                className="mb-3 border-0 px-2.5 py-1 text-base font-semibold tracking-tight text-black tabular-nums dark:text-white"
                style={{ background }}
              >
                {range}
              </Badge>
              <h3 className="mb-2 text-sm font-semibold">{title}</h3>
              <p className="text-muted-foreground text-sm leading-relaxed">
                {description}
              </p>
            </div>
          ))}
        </div>

        <Accordion
          type="single"
          collapsible
          className="rounded-lg border bg-foreground/[0.03]"
        >
          <AccordionItem value="calculation">
            <AccordionTrigger className="group w-full items-center gap-3 rounded-lg px-5 py-4 hover:bg-foreground/[0.03] hover:no-underline data-[state=open]:rounded-b-none [&>svg]:translate-y-0 [&>svg]:text-foreground">
              <span>
                <Calculator className="size-4 shrink-0" aria-hidden="true" />
              </span>
              <span className="flex-1 text-left">How it&apos;s calculated</span>
            </AccordionTrigger>
            <AccordionContent className="space-y-4 border-t px-5 py-5 leading-relaxed">
              <p className="font-medium">
                30-Day LTV:nCAC = gross profit per customer within 30 days ÷ new
                customer acquisition cost
              </p>
              <p className="text-muted-foreground">
                Gross profit includes the first order and any repeat orders
                within 30 days. It is sales after discounts and refunds, plus
                shipping charged to customers, minus the cost of goods sold,
                shipping costs and transaction fees. nCAC is marketing spend
                divided by the number of new customers.
              </p>
              <p className="text-muted-foreground">
                At 2.0×, every {one} spent acquiring customers generates {two}{" "}
                in gross profit, leaving {one} after acquisition costs and
                before overheads. At 1.0×, gross profit exactly covers
                acquisition costs. The healthy range starts at 2.0×.
              </p>
              <p className="text-muted-foreground">
                Compare cohorts with at least 30 days of order history. Newer
                cohorts or date ranges that exclude repeat orders can make the
                ratio appear lower. Make sure all marketing spend and costs are
                included. If acquisition spend is zero, the ratio cannot tell
                you whether acquisition costs have been recovered.
              </p>
              <p className="text-muted-foreground">
                These ranges are guidance, not net-profit targets. Read them
                alongside your margins, overheads and cash needs.
              </p>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </CardContent>
    </Card>
  );
}
